import { useEffect } from "react";
import FeatureCard from "../components/cards/FeatursCard";
import Button from "../components/Button";
import mainimage from "../components/assets/main_img.png";
import gallery1 from "../components/assets/installing-solar-panels.webp";
import gallery2 from "../components/assets/solar-work-img.jpg";
import gallery3 from "../components/assets/Top-Solar-Panels.webp";
import Cta from "../components/Cta";
import Seo from "../components/Seo";
import AOS from "aos";
import "aos/dist/aos.css";

const steps = [
  {
    number: "01",
    title: "Free Consultation",
    description: "We assess your energy needs and provide a free, no-obligation quote.",
    icon: "💬"
  },
  {
    number: "02",
    title: "Design & Installation",
    description: "Our expert team designs and installs your solar system with minimal disruption.",
    icon: "⚡"
  },
  {
    number: "03",
    title: "Enjoy the Savings",
    description: "Start generating your own power and saving money immediately.",
    icon: "💰"
  },
];

const testimonials = [
  {
    name: "Ravi Patel",
    feedback: "Boost Solar made the whole process simple and stress-free. My electricity bills are half of what they used to be!",
    role: "Homeowner"
  },
  {
    name: "Priya Shah",
    feedback: "Professional, reliable, and affordable. Highly recommend them for anyone considering solar.",
    role: "Business Owner"
  },
  {
    name: "Amit Kumar",
    feedback: "Excellent service from start to finish. The team was knowledgeable and the installation was flawless.",
    role: "Industrial Client"
  },
];

const galleryImages = [
  gallery1,
  gallery2,
  gallery3
];
const stats = [
  { number: "500+", label: "Happy Clients", suffix: "" },
  { number: "2", label: "Installed Capacity", suffix: "MW" },
  { number: "50", label: "Saved for Customers", suffix: "Lakhs+" },
];

const HomePage = () => {
  useEffect(() => {
    AOS.init({
      duration: 1000,
      easing: "ease-in-out",
      once: true,
      mirror: false
    });
  }, []);

  return (
    <>
      <Seo
        title="KK Solar - Best Solar Company in Kutch, Gujarat | Rooftop Solar Installation"
        description="Boost Solar provides rooftop solar installations in Kutch, Gujarat with expert design, installation, and subsidy support up to ₹78,000."
        url="https://kksolar.co.in/"
      />
      <div className="max-w-8xl mt-24  mx-auto overflow-hidden">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-br from-background via-white to-opsecondary min-h-screen flex items-center">
          <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
          <div className="max-w-7xl mx-auto px-6 py-20 text-center lg:text-left flex flex-col lg:flex-row gap-12 items-center relative z-10">          <div
            className="flex-1 space-y-8"
            data-aos="fade-right"
            data-aos-delay="200"
          >
            <div className="inline-flex items-center px-4 py-2 bg-opprimary/10 rounded-full border border-opprimary/20">
              <span className="w-2 h-2 bg-opprimary rounded-full mr-2 animate-pulse"></span>
              <span className="text-opprimary font-semibold text-sm">Leading Solar Company in Kutch</span>
            </div>

            <h1 className="text-4xl lg:text-6xl font-bold text-primary leading-tight">
              Install solar and receive a rooftop solar subsidy of up to{" "}
              <span className="text-opprimary bg-gradient-to-r from-opprimary to-orange-500 bg-clip-text text-transparent">
                ₹78,000*
              </span>
            </h1>

            <p className="text-gray-700 text-lg max-w-2xl leading-relaxed">
              Being a top solar company in Kutch, Boost Solar assists you in switching to clean electricity
              for your house, business, or industrial needs. Lower energy costs and increase savings with
              the best solar panel pricing in Kutch.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button variant="solidorange" className="transform hover:scale-105 transition-all duration-300"
                href="/solar-calculator">
                OBTAIN FREE ESTIMATES
              </Button>
              <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white"
                href="https://www.myscheme.gov.in/schemes/pmsgmb">
                Learn More
              </Button>
            </div>
          </div>

            <div
              className="flex-1 relative"
              data-aos="fade-left"
              data-aos-delay="400"
            >
              <div className="relative">
                <img
                  src={mainimage}
                  alt="Solar Installation"
                  className="w-full max-w-md lg:max-w-2xl object-contain transform hover:scale-105 transition-transform duration-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <section className="bg-gradient-to-r from-primary to-secondary py-20 relative overflow-hidden">
          <div className="absolute inset-0 bg-sparkle-pattern opacity-10"></div>
          <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-8 text-center relative z-10">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="text-white space-y-4"
                data-aos="zoom-in"
                data-aos-delay={index * 200}
              >
                <div className="relative inline-block">
                  <h3 className="text-5xl lg:text-6xl font-bold relative">
                    {stat.number}
                    <span className="text-opprimary text-2xl">{stat.suffix}</span>
                  </h3>
                  <div className="absolute -inset-4 bg-white/10 rounded-full blur-lg"></div>
                </div>
                <p className="text-lg text-white/80 font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-background relative overflow-hidden">
          <div className="absolute top-0 left-0 w-72 h-72 bg-opprimary/5 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary/5 rounded-full translate-x-1/2 translate-y-1/2"></div>

          <div className="text-center mb-16 relative z-10">
            <div
              data-aos="fade-up"
              data-aos-delay="200"
            >
              <h2 className="text-4xl lg:text-5xl font-bold text-primary mb-6">
                Why Choose <span className="text-opprimary relative">Boost Solar?
                  <span className="absolute bottom-2 left-0 w-full h-2 bg-opprimary/20 -z-10"></span>
                </span>
              </h2>
              <p className="mt-4 text-gray-600 text-lg max-w-3xl mx-auto leading-relaxed">
                From cost savings to clean energy, discover the benefits of working with
                Gujarat's most trusted solar partner.
              </p>
            </div>
          </div>

          <div
            data-aos="fade-up"
            data-aos-delay="400"
          >
            <FeatureCard />
          </div>
        </section>

        {/* Split Layout Section */}
        <section className="py-20 bg-white relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
            <div
              className="relative"
              data-aos="fade-right"
              data-aos-delay="200"
            >
              <div className="relative group">
                <img
                  src="https://thearchitectsdiary.com/wp-content/uploads/2020/12/6-Advantages-of-Installing-Solar-Panels-On-Your-Home-1024x683.jpg"
                  alt="Solar Installation"
                  className="rounded-2xl shadow-2xl w-full transform group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent rounded-2xl"></div>
              </div>
            </div>

            <div
              className="space-y-6"
              data-aos="fade-left"
              data-aos-delay="400"
            >
              <div className="inline-flex items-center px-4 py-2 bg-opprimary/10 rounded-full border border-opprimary/20">
                <span className="text-opprimary font-semibold text-sm">Our Mission</span>
              </div>

              <h2 className="text-4xl lg:text-5xl font-bold text-primary leading-tight">
                Powering a <span className="text-opprimary">Sustainable</span> Future
              </h2>

              <p className="text-gray-600 text-lg leading-relaxed">
                Our mission is to provide affordable, high-quality solar solutions that empower communities
                and reduce environmental impact. Join us in creating a cleaner, greener tomorrow.
              </p>

              <div className="flex flex-col justify-center sm:flex-row gap-4 pt-4">
                <a href="https://pmsuryaghar.gov.in/#/" target="_blank" rel="noopener noreferrer">
                  <Button variant="solidorange" className="transform hover:scale-105 transition-all"
                    href="https://www.myscheme.gov.in/schemes/pmsgmb">
                    Government Scheme
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="bg-gradient-to-b from-background to-white py-20 relative overflow-hidden">
          <div className="absolute top-10 left-10 w-32 h-32 bg-opprimary/10 rounded-full blur-xl"></div>
          <div className="absolute bottom-10 right-10 w-40 h-40 bg-secondary/10 rounded-full blur-xl"></div>

          <div className="max-w-7xl mx-auto px-6 text-center relative z-10">
            <div
              className="mb-16"
              data-aos="fade-up"
            >
              <h2 className="text-4xl lg:text-5xl font-bold text-primary mb-6">How It Works</h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                Simple, transparent process from consultation to energy savings
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 relative">
              {/* Connecting Line */}
              {/* <div className="hidden md:block absolute top-20 left-1/4 right-1/4 h-0.5 bg-gradient-to-r from-transparent via-opprimary to-transparent"></div> */}

              {steps.map((step, index) => (
                <div
                  key={index}
                  className="relative group"
                  data-aos="flip-up"
                  data-aos-delay={index * 200}
                >
                  <div className="bg-white p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-4 border border-gray-100 relative overflow-hidden">
                    {/* Background Decor */}
                    <div className="absolute top-0 right-0 w-20 h-20 bg-opprimary/5 rounded-full -translate-y-10 translate-x-10"></div>

                    {/* Step Number */}
                    <div className="text-6xl font-bold text-opprimary/10 mb-4 absolute top-4 right-4">
                      {step.number}
                    </div>

                    {/* Icon */}
                    <div className="text-4xl mb-6 relative z-10">{step.icon}</div>

                    <h3 className="text-xl font-bold text-primary mb-4 relative z-10">
                      {step.title}
                    </h3>

                    <p className="text-gray-600 leading-relaxed relative z-10">
                      {step.description}
                    </p>

                    {/* Hover Effect */}
                    <div className="absolute inset-0 bg-gradient-to-br from-opprimary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-20 bg-white relative overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
          <div className="max-w-7xl mx-auto px-6 text-center relative z-10">
            <div
              className="mb-16"
              data-aos="fade-up"
            >
              <h2 className="text-4xl lg:text-5xl font-bold text-primary mb-6">
                What Our <span className="text-opprimary">Clients</span> Say
              </h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                Don't just take our word for it - hear from our satisfied customers
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <div
                  key={index}
                  className="group"
                  data-aos="zoom-in"
                  data-aos-delay={index * 200}
                >
                  <div className="bg-background p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-gray-100 relative">
                    {/* Quote Icon */}
                    <div className="text-4xl text-opprimary/20 mb-4">"</div>

                    <p className="text-gray-700 text-lg italic mb-6 leading-relaxed">
                      "{testimonial.feedback}"
                    </p>

                    <div className="border-t border-gray-200 pt-4">
                      <h4 className="font-bold text-primary text-lg">{testimonial.name}</h4>
                      <p className="text-opprimary text-sm font-medium">{testimonial.role}</p>
                    </div>

                    {/* Hover Effect */}
                    <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-opprimary to-secondary transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Gallery Section */}
        <section className="bg-background py-20 relative overflow-hidden">
          <div className="max-w-8xl mx-auto px-6 text-center relative z-10">
            <div
              className="mb-16"
              data-aos="fade-up"
            >
              <h2 className="text-4xl lg:text-5xl font-bold text-primary mb-6">
                Our <span className="text-opprimary">Projects</span>
              </h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                Explore our successful solar installations across residential, commercial, and industrial sectors
              </p>
            </div>

            <div
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              {galleryImages.map((img, index) => (
                <div
                  key={index}
                  className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2"
                >
                  <img
                    src={img}
                    alt={`Project ${index + 1}`}
                    className="w-full h-80 object-cover transform group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-primary/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                    <div className="p-6 text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                      <h3 className="font-bold text-lg mb-2">Project {index + 1}</h3>
                      <p className="text-white/80 text-sm">Solar Installation</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* New CTA Component */}
        <Cta />

        <style jsx>{`
        .bg-grid-pattern {
          background-image: linear-gradient(rgba(2, 26, 92, 0.1) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(2, 26, 92, 0.1) 1px, transparent 1px);
          background-size: 50px 50px;
        }
      `}</style>
      </div>
    </>
  );
};

export default HomePage;